import React from 'react';
import { useSelector } from 'react-redux';
import { gql, useQuery } from '@apollo/client';
import { tr } from '~/app/ui/lang';
import Button from '~/app/ui/Button';
import EmptyState from '~/app/ui/EmptyState';
import { Page, PageHeader, Section, Card } from '~/app/ui/Page';
import useCopyText from '~/pages/components/PageEditor/aiWidget/useCopyText';
import AiGuideEditor from './AiGuideEditor';
import GuideDocument from './GuideDocument';
import useGuideStyles from './styles';

export const AI_GUIDE_TEXT = gql`query AiGuideText { aiGuideText }`;

/**
 * The guide AI assistants read before working in Genesis, and how to point an
 * AI tool at it. Super admins also edit it here; changes apply at once, with
 * no redeploy.
 */
const AiGuideScreen: React.FC = () => {
  const c = useGuideStyles();
  const isSuperAdmin = useSelector((state: { app: { user?: { isSuperAdmin?: boolean } } }) => !!state.app.user?.isSuperAdmin);
  const { data, loading, error, refetch } = useQuery<{ aiGuideText: string }>(AI_GUIDE_TEXT, { fetchPolicy: 'network-only' });
  const { copy, copied } = useCopyText();
  const [copyFailed, setCopyFailed] = React.useState(false);
  const origin = window.location.origin;
  const instruction = `When I ask you to do something in Genesis (${origin}), first open ${origin}/llms.txt in my browser, read all of it and follow it. If it asks me to sign in, wait for me.`;
  const text = data?.aiGuideText || '';

  const doCopy = async (key: string, value: string) => setCopyFailed(!(await copy(key, value)));

  let guide: React.ReactNode;
  if (error && !text) guide = <EmptyState tone="error" title="Couldn’t load the guide" action={<Button icon="refresh" onClick={() => refetch()}>Try again</Button>}>{error.message}</EmptyState>;
  else if (loading && !text) guide = <EmptyState icon="hourglass_empty" title="Loading…" compact />;
  else guide = <GuideDocument text={text} />;

  return (
    <Page testId="ai-guide-screen">
      <PageHeader
        title={tr('AI guide', 'AI 指南')}
        description="The instructions Genesis gives AI assistants such as Claude before they work here: where things are, how to do common jobs, and what to ask you first. Reading or copying this page changes nothing on your sites."
        actions={<Button href="/llms.txt" target="_blank" icon="open_in_new" data-testid="ai-guide-open">Open as text</Button>}
      />
      <Section title="Set up your AI tool" testId="ai-guide-setup">
        <Card>
          <div className={c.steps}>
            <div className={c.step}>
              <h3 className={c.stepTitle}>Claude, ChatGPT and other AI apps: connect Genesis</h3>
              <p className={c.muted}>The best way: add Genesis to your AI app as a connector. It then works in Genesis as you, straight from your chat, and reads this guide itself.</p>
              <div className={c.row}>
                <Button size="sm" icon="hub" href="/admin/connect" data-testid="ai-guide-connect">Connect your AI</Button>
              </div>
            </div>
            <div className={c.step}>
              <h3 className={c.stepTitle}>Assistants that use your browser (Claude in Chrome and similar)</h3>
              <p className={c.muted}>They can click through Genesis for you, but they don’t find this guide on their own. Add this line to your assistant’s custom instructions once, or start a conversation with it:</p>
              <p className={c.instruction} data-testid="ai-guide-instruction">{instruction}</p>
              <div className={c.row}>
                <Button size="sm" icon={copied === 'instruction' ? 'check' : 'content_copy'} onClick={() => doCopy('instruction', instruction)} data-testid="copy-instruction">
                  {copied === 'instruction' ? 'Copied' : 'Copy instruction'}
                </Button>
              </div>
            </div>
            <div className={c.step}>
              <h3 className={c.stepTitle}>Chat assistants that can’t open Genesis</h3>
              <p className={c.muted}>Copy the guide into the chat. It includes the rules for writing sections in the house style, so the code they write pastes cleanly into Genesis.</p>
              <div className={c.row}>
                <Button size="sm" icon={copied === 'guide' ? 'check' : 'content_copy'} onClick={() => doCopy('guide', text)} disabled={!text} data-testid="copy-guide">
                  {copied === 'guide' ? 'Copied' : 'Copy the guide'}
                </Button>
              </div>
            </div>
            {copyFailed && <div className={c.error} role="alert">Your browser didn’t allow copying. Select the text and copy it yourself.</div>}
          </div>
        </Card>
      </Section>
      <Section title="What your AI reads" hint="Laid out here for reading. It’s written for whoever opens it, so yours includes what you may do and the categories you can create pages in. “Open as text” shows it exactly as an AI gets it." testId="ai-guide-preview">
        {guide}
      </Section>
      {isSuperAdmin && (
        <Section title="Edit the guide" hint="Only super admins see this. Changes reach AI assistants the next time they open the guide. No deployment needed." testId="ai-guide-edit">
          <Card>
            <AiGuideEditor />
          </Card>
        </Section>
      )}
    </Page>
  );
};

export default AiGuideScreen;
